import { FunctionComponent, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChannelAppTabAction,
  Story,
  StoryAccess,
  StorySection,
  StoryType,
} from '../../../services';
import { useAppTabsLabel } from '../../hooks';

import classes from './StoryTags.module.scss';

interface StoryTagsProps {
  story: Story;
}

export const StoryTags: FunctionComponent<StoryTagsProps> = memo(({ story }) => {
  const { t } = useTranslation();

  const { getAppTabsLabel } = useAppTabsLabel();

  const { type, settings, sources = [] } = { ...story };

  const {
    access,
    section,
    horizontalDisplayView,
    allowAccessToEditorLimited,
    allowAccessToReader,
  } = settings;

  const isReadOnly = useMemo(() => type === StoryType.READONLY, [type]);

  const isSync = useMemo(
    () => Boolean(!isReadOnly && sources.length),
    [isReadOnly, sources.length]
  );

  return (
    <div className={classes['story-tags']}>
      {section.map((key) => {
        switch (key) {
          case StorySection.WEB_FEED:
            return (
              <span key={key}>
                #{`${getAppTabsLabel(ChannelAppTabAction.NEWS_FEED)} ${t('common.web')}`}
              </span>
            );
          case StorySection.MOBILE_FEED:
            return (
              <span key={key}>
                #{`${getAppTabsLabel(ChannelAppTabAction.NEWS_FEED)} ${t('common.mobile')}`}
              </span>
            );
          case StorySection.MIX_LIST:
            return <span key={key}>#{getAppTabsLabel(ChannelAppTabAction.STORIES)}</span>;
          case StorySection.MIX_LIST_2:
            return <span key={key}>#{getAppTabsLabel(ChannelAppTabAction.STORIES_2)}</span>;
          case StorySection.MIX_LIST_3:
            return <span key={key}>#{getAppTabsLabel(ChannelAppTabAction.STORIES_3)}</span>;
          case StorySection.MIX_LIST_4:
            return <span key={key}>#{getAppTabsLabel(ChannelAppTabAction.STORIES_4)}</span>;
          case StorySection.MIX_LIST_5:
            return <span key={key}>#{getAppTabsLabel(ChannelAppTabAction.STORIES_5)}</span>;
          case StorySection.PINNED:
            return <span key={key}>#{`${getAppTabsLabel(ChannelAppTabAction.PINNED_STORY)}`}</span>;
          case StorySection.PINNED_2:
            return <span key={key}>#{getAppTabsLabel(ChannelAppTabAction.PINNED_STORY_2)}</span>;
          case StorySection.PINNED_3:
            return <span key={key}>#{getAppTabsLabel(ChannelAppTabAction.PINNED_STORY_3)}</span>;
          case StorySection.PINNED_4:
            return <span key={key}>#{getAppTabsLabel(ChannelAppTabAction.PINNED_STORY_4)}</span>;
          case StorySection.PINNED_5:
            return <span key={key}>#{getAppTabsLabel(ChannelAppTabAction.PINNED_STORY_5)}</span>;
          case StorySection.HORIZONTAL:
            return (
              <span key={key}>
                #{t(`storyTags.${key}`, { position: horizontalDisplayView?.position })}
              </span>
            );
          default:
            return null;
        }
      })}
      {isReadOnly && <span>#{t(`storyType.${StoryType.READONLY}`)}</span>}
      {isSync && <span>#{t('common.sync')}</span>}
      {allowAccessToEditorLimited && <span>#{t('roles.editor-limited')}</span>}
      {allowAccessToReader && <span>#{t('roles.reader')}</span>}
      {access.includes(StoryAccess.INDIVIDUAL) && <span>#{t('storyAccess.INDIVIDUAL')}</span>}
    </div>
  );
});
